/* eslint-disable @typescript-eslint/no-use-before-define */
import type { FunctionComponent } from 'react';

import { sendErrorEvent } from '@trello/error-reporting';
import { Spinner } from '@trello/nachos/spinner';
import type { PIIString } from '@trello/privacy';

import { useMemberLocaleQuery } from 'app/src/components/PowerUpDirectory/MemberLocaleQuery.generated';
import {
  getDefaultLocale,
  getTemplateLanguageEnabledFromMemberLocale,
} from './Helpers';
import { TemplatesGalleryContent } from './TemplatesGalleryContent';
import type { TemplateLanguage } from './types';

// eslint-disable-next-line @trello/less-matches-component
import styles from './Spinner.less';

const loadingSpinner = (
  <div className={styles.spinnerContainer}>
    <Spinner />
  </div>
);

export const TemplatesContainerLoggedIn: FunctionComponent<{
  locale?: PIIString;
  languages: TemplateLanguage[];
  category?: string;
  shortLink?: string;
  isInAppGallery: boolean;
}> = ({ locale, languages, category, shortLink, isInAppGallery }) => {
  if (locale) {
    return (
      <TemplatesGalleryContent
        templateLanguage={getTemplateLanguageEnabledFromMemberLocale(
          locale,
          languages,
        )}
        languages={languages}
        category={category}
        shortLink={shortLink}
        isInAppGallery={isInAppGallery}
      />
    );
  } else {
    return (
      <TemplatesContainerLoggedInFetchLocale
        languages={languages}
        category={category}
        shortLink={shortLink}
        isInAppGallery={isInAppGallery}
      />
    );
  }
};

const TemplatesContainerLoggedInFetchLocale: FunctionComponent<{
  languages: TemplateLanguage[];
  category?: string;
  shortLink?: string;
  isInAppGallery: boolean;
}> = ({ languages, category, shortLink, isInAppGallery }) => {
  const {
    data: memberLocaleData,
    loading: loadingMemberLocale,
    error: memberLocaleError,
  } = useMemberLocaleQuery({
    waitOn: ['MemberHeader'],
  });

  if (loadingMemberLocale) {
    return loadingSpinner;
  } else if (memberLocaleData) {
    return (
      <TemplatesGalleryContent
        templateLanguage={getTemplateLanguageEnabledFromMemberLocale(
          memberLocaleData?.member?.prefs?.locale || getDefaultLocale(),
          languages,
        )}
        languages={languages}
        category={category}
        shortLink={shortLink}
        isInAppGallery={isInAppGallery}
      />
    );
  } else if (memberLocaleError) {
    sendErrorEvent(new Error(memberLocaleError.message), {
      tags: {
        ownershipArea: 'trello-web-eng',
      },
      extraData: {
        networkError: memberLocaleError.networkError
          ? memberLocaleError.networkError.message
          : false,
        extraInfo: memberLocaleError.extraInfo,
        graphQLErrors: memberLocaleError.graphQLErrors.toString(),
        stack: memberLocaleError.stack ? memberLocaleError.stack : false,
        name: memberLocaleError.name,
      },
    });
  }

  return null;
};
